import { ref } from "vue";

export type FetchPageFn<T> = (offset: number, pageSize: number) => Promise<T[] | null>;

export interface PaginatedLoaderOptions<T> {
  pageSize?: number;
  onBatch?: (items: T[], offset: number) => void | Promise<void>;
  signal?: AbortSignal;
}

export function usePaginatedLoader() {
  const isLoading = ref(false);

  async function loadAll<T>(fetchPage: FetchPageFn<T>, options: PaginatedLoaderOptions<T> = {}): Promise<T[]> {
    const pageSize = options.pageSize ?? 300;
    const all: T[] = [];
    let offset = 0;

    isLoading.value = true;

    try {
      while (!options.signal?.aborted) {
        const items = await fetchPage(offset, pageSize);

        if (items == null) break;
        if (items.length === 0) break;

        if (options.onBatch) {
          await options.onBatch(items, offset);
        } else {
          all.push(...items);
        }

        if (items.length < pageSize) break;
        offset += items.length;
      }
    } finally {
      isLoading.value = false;
    }

    return all;
  }

  return { loadAll, isLoading };
}
